import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Pencil, Trash2, Plus, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, eur, priceLabel } from "@/lib/api";

const EMPTY_FORM = {
  name: "",
  category: "orologi",
  price: "",
  reference: "",
  description: "",
  image: "",
  images: "",
  year: "",
  condition: "Ottimo",
  stock: 1,
  featured: false,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("prodotti");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [valutazioni, setValutazioni] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | product object
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const loadRequested = useRef(false);

  const loadData = async () => {
    setDataLoading(true);
    await Promise.allSettled([
      api.get("/products").then(({ data }) => setProducts(data)),
      api.get("/orders").then(({ data }) => setOrders(data)),
      api.get("/valutazioni").then(({ data }) => setValutazioni(data)),
    ]);
    setDataLoading(false);
  };

  useEffect(() => {
    if (loadRequested.current) return;
    loadRequested.current = true;
    if (location.state?.user) {
      loadData();
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        loadData();
      })
      .catch(() => {
        localStorage.removeItem("oep_token");
        navigate("/admin");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem("oep_token");
      navigate("/admin");
    }
  };

  const startEdit = (product) => {
    setEditing(product);
    setForm({ ...product, images: (product.images || []).join("\n") });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_FORM);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      stock: parseInt(form.stock, 10) || 0,
      images: String(form.images || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    };
    try {
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Prodotto creato");
      } else {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Prodotto aggiornato");
      }
      setEditing(null);
      loadData();
    } catch {
      toast.error("Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Eliminare "${product.name}"?`)) return;
    try {
      await api.delete(`/products/${product.id}`);
      toast.success("Prodotto eliminato");
      loadData();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-void">
        <p className="font-serif text-2xl italic text-muted">Verifica accesso…</p>
      </div>
    );
  }

  const field = "w-full border border-line bg-transparent px-4 py-3 text-sm text-ivory outline-none transition-colors duration-300 focus:border-gold";
  const label = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted";

  return (
    <div className="min-h-screen bg-void px-6 py-10 lg:px-12" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="font-serif text-3xl tracking-tight text-ivory">
            Atelier <span className="italic text-gold">Gestione</span>
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-muted">{user.email}</p>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" data-testid="admin-view-site" className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:text-gold">
            Vedi il sito
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 border border-line px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            <LogOut size={13} /> Esci
          </button>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        {[
          ["prodotti", `Prodotti (${products.length})`],
          ["ordini", `Ordini (${orders.length})`],
          ["valutazioni", `Valutazioni (${valutazioni.length})`],
        ].map(([key, lbl]) => (
          <button
            key={key}
            data-testid={`tab-${key}`}
            onClick={() => setTab(key)}
            className={`rounded-full border px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === key ? "border-gold bg-gold text-void" : "border-line text-muted hover:border-gold hover:text-gold"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {tab === "prodotti" && (
        <>
          <div className="mt-8">
            <button
              onClick={startNew}
              data-testid="add-product-button"
              className="flex items-center gap-2 border border-gold px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-void"
            >
              <Plus size={14} /> Nuovo Prodotto
            </button>
          </div>

          {editing && (
            <form onSubmit={handleSave} className="mt-8 border border-line bg-surface p-8" data-testid="product-form">
              <p className="font-serif text-2xl text-ivory">{editing === "new" ? "Nuovo prodotto" : `Modifica: ${editing.name}`}</p>
              <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  <label className={label}>Nome</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="form-name-input" />
                </div>
                <div>
                  <label className={label}>Categoria</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field} data-testid="form-category-select">
                    <option value="orologi">Orologi</option>
                    <option value="gioielli">Gioielli</option>
                  </select>
                </div>
                <div>
                  <label className={label}>Prezzo (EUR, 0 = su richiesta)</label>
                  <input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={field} data-testid="form-price-input" />
                </div>
                <div>
                  <label className={label}>Referenza</label>
                  <input required value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className={field} data-testid="form-reference-input" placeholder="OEP-14" />
                </div>
                <div>
                  <label className={label}>Anno</label>
                  <input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className={field} data-testid="form-year-input" />
                </div>
                <div>
                  <label className={label}>Condizione</label>
                  <input value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className={field} data-testid="form-condition-input" />
                </div>
                <div>
                  <label className={label}>Disponibilità</label>
                  <input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={field} data-testid="form-stock-input" />
                </div>
                <div className="lg:col-span-3">
                  <label className={label}>URL Foto Principale (o /api/media/…)</label>
                  <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={field} data-testid="form-image-input" placeholder="/api/media/…" />
                </div>
                <div className="lg:col-span-3">
                  <label className={label}>Foto aggiuntive galleria (una URL per riga)</label>
                  <textarea rows={4} value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className={`${field} font-mono text-xs`} data-testid="form-images-textarea" placeholder={"/api/media/…\n/api/media/…"} />
                </div>
                <div className="lg:col-span-3">
                  <label className={label}>Descrizione</label>
                  <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} data-testid="form-description-input" />
                </div>
                <label className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#D4AF37]" data-testid="form-featured-checkbox" />
                  In vetrina (home)
                </label>
              </div>
              <div className="mt-8 flex gap-4">
                <button type="submit" disabled={saving} data-testid="product-save-button" className="border border-gold px-8 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-void disabled:opacity-50">
                  {saving ? "Salvataggio…" : "Salva"}
                </button>
                <button type="button" onClick={() => setEditing(null)} data-testid="product-cancel-button" className="border border-line px-8 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:text-ivory">
                  Annulla
                </button>
              </div>
            </form>
          )}

          <div className="mt-10 overflow-x-auto border border-line" data-testid="products-table">
            <table className="w-full min-w-[760px] text-left">
              <thead>
                <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
                  <th className="px-5 py-4">Pezzo</th>
                  <th className="px-5 py-4">Referenza</th>
                  <th className="px-5 py-4">Categoria</th>
                  <th className="px-5 py-4">Prezzo</th>
                  <th className="px-5 py-4">Foto</th>
                  <th className="px-5 py-4">Vetrina</th>
                  <th className="px-5 py-4 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {dataLoading && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center font-serif text-xl italic text-muted" data-testid="products-loading">
                      Caricamento prodotti…
                    </td>
                  </tr>
                )}
                {!dataLoading && products.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center font-serif text-xl italic text-muted" data-testid="products-empty">
                      Nessun prodotto presente.
                    </td>
                  </tr>
                )}
                {!dataLoading && products.map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.reference}`} className="border-b border-line transition-colors duration-300 hover:bg-elevated">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-4">
                        {p.image ? (
                          <img src={p.image} alt="" className="h-14 w-11 bg-elevated object-cover" />
                        ) : (
                          <div className="flex h-14 w-11 items-center justify-center bg-elevated font-mono text-[10px] text-muted">—</div>
                        )}
                        <span className="font-serif text-lg text-ivory">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 font-mono text-xs text-gold">{p.reference}</td>
                    <td className="px-5 py-4 font-mono text-xs uppercase text-muted">{p.category}</td>
                    <td className="px-5 py-4 font-mono text-sm text-ivory">{priceLabel(p)}</td>
                    <td className="px-5 py-4 font-mono text-xs text-muted">{1 + (p.images || []).length}</td>
                    <td className="px-5 py-4 font-mono text-xs text-muted">{p.featured ? "Sì" : "—"}</td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-3">
                        <button onClick={() => startEdit(p)} data-testid={`edit-product-${p.reference}`} aria-label="Modifica" className="text-muted transition-colors duration-300 hover:text-gold">
                          <Pencil size={15} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => handleDelete(p)} data-testid={`delete-product-${p.reference}`} aria-label="Elimina" className="text-muted transition-colors duration-300 hover:text-red-400">
                          <Trash2 size={15} strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "ordini" && (
        <div className="mt-10 overflow-x-auto border border-line" data-testid="orders-table">
          <table className="w-full min-w-[760px] text-left">
            <thead>
              <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
                <th className="px-5 py-4">Data</th>
                <th className="px-5 py-4">Articoli</th>
                <th className="px-5 py-4">Totale</th>
                <th className="px-5 py-4">Stato</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center font-serif text-xl italic text-muted" data-testid="orders-empty">
                    Nessun ordine ancora.
                  </td>
                </tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id.slice(0, 8)}`} className="border-b border-line">
                  <td className="px-5 py-4 font-mono text-xs text-muted">{new Date(o.created_at).toLocaleString("it-IT")}</td>
                  <td className="px-5 py-4 text-sm text-ivory">
                    {o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}
                  </td>
                  <td className="px-5 py-4 font-mono text-sm text-gold">{eur(o.amount)}</td>
                  <td className="px-5 py-4">
                    <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${o.payment_status === "paid" ? "text-gold" : "text-muted"}`}>
                      {o.payment_status === "paid" ? "Pagato" : o.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "valutazioni" && (
        <div className="mt-10 overflow-x-auto border border-line" data-testid="valutazioni-table">
          <table className="w-full min-w-[760px] text-left">
            <thead>
              <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
                <th className="px-5 py-4">Data</th>
                <th className="px-5 py-4">Nome</th>
                <th className="px-5 py-4">Contatto</th>
                <th className="px-5 py-4">Orologio</th>
                <th className="px-5 py-4">Note</th>
              </tr>
            </thead>
            <tbody>
              {valutazioni.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center font-serif text-xl italic text-muted" data-testid="valutazioni-empty">
                    Nessuna richiesta di valutazione ancora.
                  </td>
                </tr>
              )}
              {valutazioni.map((v) => (
                <tr key={v.id} data-testid={`valutazione-row-${v.id.slice(0, 8)}`} className="border-b border-line">
                  <td className="px-5 py-4 font-mono text-xs text-muted">{new Date(v.created_at).toLocaleString("it-IT")}</td>
                  <td className="px-5 py-4 text-sm text-ivory">{v.name}</td>
                  <td className="px-5 py-4 font-mono text-xs text-gold">{v.contact}</td>
                  <td className="px-5 py-4 text-sm text-ivory">{v.watch}</td>
                  <td className="max-w-xs px-5 py-4 text-sm text-muted">{v.message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
